/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { AppConfig } from '@/types/config';

type YottaaConfig = AppConfig['yottaa'];

/**
 * Returns a browser-safe Yottaa script URL, or undefined when unconfigured.
 * The legacy SFCC integration renders only when its URL preference is non-empty;
 * Storefront Next additionally requires HTTPS to align with strict CSP.
 */
export function getYottaaScriptUrl(config: YottaaConfig): string | undefined {
    const scriptUrl = config?.scriptUrl?.trim();
    if (!scriptUrl) return undefined;

    try {
        const url = new URL(scriptUrl);
        return url.protocol === 'https:' ? url.toString() : undefined;
    } catch {
        return undefined;
    }
}
